import EventEmitter from 'events';
import * as ActionTypes from 'action_types/HomeApplicationActionTypes';
import Dispatcher from 'dispatcher';
import _ from 'lodash';
import HomeApplication from 'models/HomeApplication';
import Asset from 'models/homes/Asset';

const CHANGE = 'change';
const CANCEL_CHANGE = 'cancel_change';
const ASSET_CHANGE = 'asset_change';

let _home_application = null;
let _home_application_data = {};
let _homes_applications = {};
let _assets = {};
let code = null;
let _errorPromoCode = null;
let _errorUpdateHomeApplication = null;
let _assetUploadError = null;
let _maxAssetSize = null;

class HomeApplicationStore extends EventEmitter {
  constructor(): void {
    super();
    Dispatcher.register(this._actionsHandler);
  }

  _getHomeApplications = () => {
    return _.values(_homes_applications);
  };

  getHomeApplication = () => {
    return _home_application;
  };

  getHomeApplicationData = () => {
    return _home_application_data;
  };

  getHomeApplicationAssetsByType = (asset_type: string) => {
    return _assets[asset_type] || [];
  };

  getHomeApplicationAssets = () => {
    return _assets;
  };

  getAssetUploadError = () => {
    return _assetUploadError;
  };

  // En MB. null mientras no se haya resuelto /utilities/max_asset_size.json; quien valide debe
  // tratar ese caso como "sin limite conocido" y dejar que el backend rechace con 422.
  getMaxAssetSize = () => {
    return _maxAssetSize;
  };

  _setHomeApplications = (homes_applications: []) => {
    _homes_applications = {};
    _.forEach(homes_applications, home_application => {
      _homes_applications[home_application.id] = home_application;
    });
  };

  _setHomeApplicationAssets = assets => {
    const keys = Object.keys(assets);
    for (let key of keys) {
      if (_assets[key] !== undefined) {
        const ids = _assets[key].map(asset => asset.id);
        for (let asset of assets[key]) {
          if (!_.includes(ids, asset.id)) {
            _assets[key].push(asset);
          }
        }
      } else {
        _assets[key] = assets[key];
      }
    }
  };

  _setHomeApplication = (home_application: HomeApplication) => {
    _home_application = home_application;
  };

  _setHomeApplicationData = (home_application: HomeApplication) => {
    _home_application_data = home_application;
  };

  _emitChange = () => {
    this.emit(CHANGE);
  };

  _emitCancelChange = () => {
    this.emit(CANCEL_CHANGE);
  };

  _emitAssetsChange = () => {
    this.emit(ASSET_CHANGE);
  };

  _addTempAsset = (asset: Asset) => {
    if (_assets[asset.asset_type]) {
      _assets[asset.asset_type].push(asset);
    } else {
      _assets[asset.asset_type] = [asset];
    }
  };

  _addTrueAsset = (temp_asset_id: string, asset: Asset) => {
    if (_assets[asset.asset_type] && _assets[asset.asset_type].length > 0) {
      for (let i = 0; i < _assets[asset.asset_type].length; ++i) {
        if (_assets[asset.asset_type][i].id === temp_asset_id) {
          _assets[asset.asset_type][i] = asset;
          break;
        }
      }
    } else {
      _assets[asset.asset_type] = [asset];
    }
  };

  // El asset temporal se agrega al seleccionar el archivo y solo se reemplaza por el real en el
  // success del upload. Si el upload falla hay que sacarlo o se queda colgado en pendiente.
  _removeTempAsset = (temp_asset_id: string, asset_type: string) => {
    if (!_assets[asset_type]) return;
    _assets[asset_type] = _assets[asset_type].filter(
      asset => asset.id !== temp_asset_id
    );
  };

  _deleteAsset = (asset_id: string, asset_type: string) => {
    const assetsIds = _assets[asset_type].map(asset => asset.id);
    if (assetsIds.indexOf(asset_id) > -1) {
      _assets[asset_type].splice(assetsIds.indexOf(asset_id), 1);
    }
  };

  addChangeListener = (callback: () => void) => {
    this.on(CHANGE, callback);
  };

  addCancelChangeListener = (callback: () => void) => {
    this.on(CANCEL_CHANGE, callback);
  };

  removeChangeListener = (callback: () => void) => {
    this.removeListener(CHANGE, callback);
  };

  removeCancelChangeListener = (callback: () => void) => {
    this.removeListener(CANCEL_CHANGE, callback);
  };

  addAssetsChangeListener = (callback: () => void) => {
    this.on(ASSET_CHANGE, callback);
  };

  removeAssetsChangeListener = (callback: () => void) => {
    this.removeListener(ASSET_CHANGE, callback);
  };

  setCodeValidate = (_code: any): void => {
    code = _code;
  };

  getCodeValidate = (_code: any): void => {
    return code;
  };

  setCodeValidateError = (error: any): void => {
    _errorPromoCode = error;
  };

  setUpdateHomeApplicationError = (error: any): void => {
    _errorUpdateHomeApplication = error;
  };

  getCodeValidateError = (error?: any): void => {
    return _errorPromoCode;
  };

  getUpdateHomeApplicationError = (): void => {
    return _errorUpdateHomeApplication;
  };

  flushAssets = () => {
    _assets = {};
  };

  _actionsHandler = (action: any) => {
    switch (action.type) {
      case ActionTypes.LIST_HOME_APPLICATIONS: {
        this._setHomeApplications(action.home_applications);
        this._emitChange();
        break;
      }
      case ActionTypes.LIST_HOME_APPLICATION_ASSETS: {
        this._setHomeApplicationAssets(action.assets);
        this._emitAssetsChange();
        break;
      }
      case ActionTypes.EDIT_HOME_APPLICATION: {
        this._setHomeApplication(action.home_application);
        this._emitChange();
        break;
      }
      case ActionTypes.CREATE_HOME_APPLICATIONS: {
        this._setHomeApplication(action.home_application);
        this._emitChange();
        break;
      }
      case ActionTypes.UPDATE_HOME_APPLICATIONS: {
        this._setHomeApplication(action.home_application);
        this._emitChange();
        break;
      }
      case ActionTypes.UPLOAD_HOME_APPLICATION_ASSET: {
        _assetUploadError = null;
        this._addTrueAsset(action.temp_asset_id, action.asset);
        this._emitAssetsChange();
        break;
      }
      case ActionTypes.UPLOAD_HOME_APPLICATION_ASSET_FAILED: {
        _assetUploadError = {
          asset_type: action.asset_type,
          message: action.error
        };
        this._removeTempAsset(action.temp_asset_id, action.asset_type);
        this._emitAssetsChange();
        break;
      }
      case ActionTypes.SET_HOME_APPLICATION_MAX_ASSET_SIZE: {
        _maxAssetSize = action.max_asset_size;
        break;
      }
      case ActionTypes.ADD_TEMP_HOME_APPLICATION_ASSET: {
        _assetUploadError = null;
        this._addTempAsset(action.asset);
        this._emitAssetsChange();
        break;
      }
      case ActionTypes.DELETE_HOME_APPLICATION_ASSET: {
        this._deleteAsset(action.asset_id, action.asset_type);
        this._emitAssetsChange();
        break;
      }
      case ActionTypes.CANCEL_HOME_APPLICATION: {
        this._emitCancelChange();
        break;
      }
      case ActionTypes.GET_HOME_APPLICATION_VALIDATE_PROMO_CODE: {
        this.setCodeValidate(action.response);
        this.getCodeValidate();
        this._emitChange();
        break;
      }
      case ActionTypes.GET_HOME_APPLICATION_VALIDATE_PROMO_CODE_ERROR: {
        this.setCodeValidateError(action.error);
        this.getCodeValidateError();
        this._emitChange();
        break;
      }
      case ActionTypes.GET_HOME_APPLICATION_UPDATE_ERROR: {
        this.setUpdateHomeApplicationError(action.error);
        this.getUpdateHomeApplicationError();
        this._emitChange();
        break;
      }
    }
  };
}

export default new HomeApplicationStore();
